import React, { useCallback, useEffect, useState } from "react";
import { useAccount, useAccountRefresh } from "../administration/users/AccountProvider";
import { useTranslation } from "react-i18next";
import { User, USER_LANGUAGES, UserDetails, userSchema } from "../administration/users/User";
import { useAxios } from "../shared/technical/rest/AxiosProvider";
import { getUser, sendPasswordMailToUser, updateUser } from "../administration/users/UsersApi";
import { FormSection } from "../shared/components/forms/FormSection";
import { Alert, Box, Grid, InputLabel, Link, Snackbar, useMediaQuery, useTheme } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { RhfForm } from "../shared/components/forms/RhfForm";
import { WideFormTextField } from "../shared/components/forms/WideFormTextField";
import { EditButton } from "../shared/components/buttons/EditButton";
import { CancelButton } from "../shared/components/buttons/CancelButton";
import { SaveButton } from "../shared/components/buttons/SaveButton";
import { FormButtonBar } from "../shared/components/forms/FormButtonBar";
import { WideFormSelectField } from "../shared/components/forms/WideFormSelectField";
import { wideFormFieldStyles } from "../shared/components/forms/UseWideFormFieldStyles";
import Typography from "@mui/material/Typography";
import { formatDateMedium, parseDate } from "../shared/technical/dateTime/dateFormatter";

export function PersonalDataPage() {
  const axios = useAxios();
  const account = useAccount();
  const refresh = useAccountRefresh();
  const {t} = useTranslation();

  const theme = useTheme();
  const smallScreens = useMediaQuery(theme.breakpoints.down('sm'));
  const [user, setUser] = useState<User>();
  const [formReadonly, setFormReadonly] = useState<boolean>(true);
  const [snackBarOpen, setSnackBarOpen] = React.useState(false);

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: user,
    resolver: yupResolver(userSchema(t))
  });
  const {reset, register} = methods;

  useEffect(() => {
    register('id');
    register('version');
  }, [register]);

  useEffect(() => {
    getUser(axios, account.id!!).then(setUser);
  }, [account, axios])

  useEffect(() => {
    reset(user);
  }, [user, reset]);

  const handleSave = useCallback((updatedUser: UserDetails) => {
    updateUser(axios, updatedUser).then(() => {
      setFormReadonly(true);
      refresh();
    })
  }, [axios, refresh])

  const handleCancel = useCallback(() => {
    getUser(axios, account.id!!).then(setUser);
    setFormReadonly(true);
  }, [axios, account])

  const handlePasswordChange = useCallback(() => {
    sendPasswordMailToUser(axios, account.id!!).then(() => setSnackBarOpen(true));
  }, [axios, account]);

  const hasValidity = user?.validityInfo && (user.validityInfo.validTo || user.validityInfo.validFrom);
  const validity = (user?.validityInfo?.validFrom ? t('common.from') + ' ' + formatDateMedium(parseDate(user?.validityInfo?.validFrom)) : '') +
    (user?.validityInfo?.validTo ? ' ' + t('common.to') + ' ' + formatDateMedium(parseDate(user?.validityInfo?.validTo)) : '');

  return (
    <FormProvider {...methods} >
      <RhfForm id={'user-form'} onSubmit={handleSave}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          margin: smallScreens ? theme.spacing(2) : undefined,
          marginLeft: smallScreens ? theme.spacing(5) : undefined,
        }}>
          <EditButton onClick={() => setFormReadonly(false)} sx={{
            width: 'fit-content',
            marginBottom: theme.spacing(2),
            marginLeft: 'auto',
          }} disabled={!formReadonly || !account.company} />
          <FormSection title={t('PersonalData.personalDataSection')} showBorder={true} sx={{marginBottom: theme.spacing(2),}}>
            {!account.company &&
              <Alert color={"warning"} style={{marginBottom: '16px'}}>{t('PersonalData.msgNoCompanySet')}</Alert>
            }
            <Grid container={true}>
              <Grid item={true} xs={12} md={8} xl={6}>
                <WideFormTextField name={'firstName'} label={t('User.firstName')} readonly={formReadonly} />
                <WideFormTextField name={'lastName'} label={t('User.lastName')} readonly={formReadonly} />
                {account.company &&
                  <WideFormTextField name={'company.name'} label={t('Company.titleEdit')} readonly={true} />
                }
                <WideFormSelectField name={'locale'}
                                     list={USER_LANGUAGES}
                                     getListKey={(locale) => locale}
                                     getListLabel={(locale) => t('Locales.' + locale)}
                                     label={t('User.locale')}
                                     readonly={formReadonly}
                />
              </Grid>
              {!formReadonly &&
                <FormButtonBar>
                  <Grid item={true}>
                    <CancelButton onClick={handleCancel} sx={{marginLeft: 'auto',}} />
                  </Grid>
                  <Grid item={true}>
                    <SaveButton />
                  </Grid>
                </FormButtonBar>
              }
            </Grid>
          </FormSection>
          <FormSection title={t('PersonalData.securitySection')} showBorder={true}>
            <Grid container={true}>
              <Grid item={true} xs={12} md={8} xl={6}>
                <WideFormTextField name={'email'} label={t('User.email')} readonly={true} />
                <WideFormTextField name={'password'} label={t('User.password')} value={'*************'} readonly={true} />
                <Grid container={true} sx={wideFormFieldStyles(theme).container}>
                  <Grid item={true} xs={4} lg={3} xl={2} sx={wideFormFieldStyles(theme).labelContainer}>
                  </Grid>
                  <Grid item={true}>
                    <Link href={'#'} onClick={handlePasswordChange} underline="hover">{t('PersonalData.sendPasswordChangeButton')}</Link>
                  </Grid>
                </Grid>
                {hasValidity &&
                  <Grid container={true} sx={wideFormFieldStyles(theme).container}>
                    <Grid item={true} xs={4} lg={2} xl={1} sx={wideFormFieldStyles(theme).labelContainer}>
                      <InputLabel htmlFor={''} sx={wideFormFieldStyles(theme).label}>{t('User.validity')}</InputLabel>
                    </Grid>
                    <Grid item={true}>
                      <Typography variant={"body1"} sx={wideFormFieldStyles(theme).value}>
                        {validity}
                      </Typography>
                    </Grid>
                  </Grid>
                }
              </Grid>
              <Grid item={true} xs={'auto'}>
              </Grid>
            </Grid>
          </FormSection>
        </Box>
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          open={snackBarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackBarOpen(false)}
          message={t('PersonalData.passwordUpdateEmailSentMsg')}
        />
      </RhfForm>
    </FormProvider>
  );
}
